import React, { useState } from 'react';
import MediaPicker from '../../../components/admin/MediaPicker';
import { DragHandle, SortableItem, reorder } from './sortable';
import { BLOCK_TYPES, ICON_SUGGESTIONS, createBlock, getBlockType, newBlockId } from './blockTypes';
import { HOME_BUTTON_STYLES } from '../../../data/homeDefaults';

const BUTTON_STYLE_LABELS = { twitch: 'Twitch purple', youtube: 'YouTube red', primary: 'Accent', secondary: 'Outline' };

function truncate(text, max = 60) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

const ICON_LIST_ID = 'admin-icon-suggestions';

/** Render once per screen; every IconField suggests from it. */
export function IconSuggestions() {
  return (
    <datalist id={ICON_LIST_ID}>
      {ICON_SUGGESTIONS.map((icon) => <option key={icon} value={icon} />)}
    </datalist>
  );
}

export function IconField({ label = 'Icon', value, onChange }) {
  return (
    <label>
      {label}
      <span className="pbe-icon-input">
        <span className="pbe-icon-preview">{value ? <i className={value} aria-hidden="true" /> : <i className="fas fa-ban" aria-hidden="true" />}</span>
        <input value={value || ''} list={ICON_LIST_ID} placeholder="fas fa-star" onChange={(event) => onChange(event.target.value)} />
      </span>
    </label>
  );
}

/** Repeatable list of link buttons (label, link, icon, style). */
export function ButtonListFields({ listId, buttons, onChange }) {
  const list = buttons || [];
  const setButton = (index, patch) => {
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    onChange(next);
  };

  return (
    <>
      {list.map((button, index) => (
        <SortableItem
          key={index}
          listId={listId}
          index={index}
          className="tpb-repeat-block"
          onMove={(from, to) => onChange(reorder(list, from, to))}
        >
          <div className="tpb-nested-block-head">
            <DragHandle listId={listId} index={index} />
            <strong>{button.icon && <i className={button.icon} aria-hidden="true" />} {button.label || `Button ${index + 1}`}</strong>
            <button type="button" className="admin-icon-btn danger" title="Remove button" onClick={() => onChange(list.filter((_, i) => i !== index))}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
          <div className="admin-grid-2">
            <label>
              Label
              <input value={button.label || ''} onChange={(event) => setButton(index, { label: event.target.value })} />
            </label>
            <label>
              Link
              <input value={button.href || ''} placeholder="/tools or https://…" onChange={(event) => setButton(index, { href: event.target.value })} />
            </label>
            <IconField value={button.icon} onChange={(icon) => setButton(index, { icon })} />
            <label>
              Style
              <select value={button.style || 'primary'} onChange={(event) => setButton(index, { style: event.target.value })}>
                {HOME_BUTTON_STYLES.map((style) => <option key={style} value={style}>{BUTTON_STYLE_LABELS[style] || style}</option>)}
              </select>
            </label>
          </div>
        </SortableItem>
      ))}
      <button
        type="button"
        className="btn btn-secondary tpb-add-btn"
        onClick={() => onChange([...list, { label: '', href: '', icon: '', style: list.length ? 'secondary' : 'primary' }])}
      >
        <i className="fas fa-plus" aria-hidden="true" /> Add button
      </button>
    </>
  );
}

function ItemsField({ field, items, onChange, listId }) {
  const list = items || [];
  const setItem = (index, patch) => {
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    onChange(next);
  };

  return (
    <div className="pbe-items">
      <p className="pbe-field-label">{field.label} <em>({list.length})</em></p>
      {list.map((item, index) => (
        <SortableItem
          key={index}
          listId={listId}
          index={index}
          className={`tpb-repeat-block${field.compact ? ' pbe-item--compact' : ''}`}
          onMove={(from, to) => onChange(reorder(list, from, to))}
        >
          <div className="tpb-nested-block-head">
            <DragHandle listId={listId} index={index} />
            <strong>{truncate(field.summary?.(item), 40) || `${field.itemLabel} ${index + 1}`}</strong>
            <button type="button" className="admin-icon-btn" title="Duplicate" onClick={() => {
              const next = [...list];
              next.splice(index + 1, 0, { ...item });
              onChange(next);
            }}
            >
              <i className="fas fa-copy" aria-hidden="true" />
            </button>
            <button type="button" className="admin-icon-btn danger" title={`Remove ${field.itemLabel.toLowerCase()}`} onClick={() => onChange(list.filter((_, i) => i !== index))}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
          <div className={field.compact ? 'admin-grid-2' : undefined}>
            {field.fields.map((sub) => (
              <Field key={sub.key} field={sub} value={item[sub.key]} onChange={(value) => setItem(index, { [sub.key]: value })} listId={`${listId}-${sub.key}`} />
            ))}
          </div>
        </SortableItem>
      ))}
      <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => onChange([...list, { ...field.newItem }])}>
        <i className="fas fa-plus" aria-hidden="true" /> Add {field.itemLabel.toLowerCase()}
      </button>
    </div>
  );
}

function Field({ field, value, onChange, listId }) {
  switch (field.kind) {
    case 'textarea':
      return (
        <label>
          {field.label}
          <textarea rows={field.rows || 3} value={value || ''} placeholder={field.placeholder} onChange={(event) => onChange(event.target.value)} />
          {field.hint && <small className="pbe-hint">{field.hint}</small>}
        </label>
      );
    case 'select':
      return (
        <label>
          {field.label}
          <select value={value ?? field.options[0][0]} onChange={(event) => onChange(event.target.value)}>
            {field.options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
          </select>
        </label>
      );
    case 'image':
      return <div className="pbe-image-field"><MediaPicker label={field.label} value={value || ''} onChange={onChange} placeholder="Image URL, or pick from Library / Upload" /></div>;
    case 'icon':
      return <IconField label={field.label} value={value} onChange={onChange} />;
    case 'buttons':
      return (
        <div className="pbe-items">
          <p className="pbe-field-label">{field.label}</p>
          <ButtonListFields listId={listId} buttons={value} onChange={onChange} />
        </div>
      );
    case 'items':
      return <ItemsField field={field} items={value} onChange={onChange} listId={listId} />;
    default:
      return (
        <label>
          {field.label}
          <input value={value || ''} placeholder={field.placeholder} onChange={(event) => onChange(event.target.value)} />
          {field.hint && <small className="pbe-hint">{field.hint}</small>}
        </label>
      );
  }
}

function AddBlockMenu({ onAdd, label = 'Add a block', compact = false }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`pbe-add${compact ? ' pbe-add--compact' : ''}${open ? ' is-open' : ''}`}>
      <button type="button" className="pbe-add-toggle" onClick={() => setOpen((value) => !value)}>
        <i className={`fas fa-${open ? 'times' : 'plus'}`} aria-hidden="true" /> {open ? 'Close' : label}
      </button>
      {open && (
        <div className="pbe-palette">
          {BLOCK_TYPES.map((type) => (
            <button
              key={type.type}
              type="button"
              className="pbe-palette-item"
              onClick={() => {
                onAdd(type.type);
                setOpen(false);
              }}
            >
              <i className={type.icon} aria-hidden="true" />
              <span>
                <strong>{type.label}</strong>
                <small>{type.description}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Editor for a list of PageBlocks: add any block type anywhere, reorder by drag or
 * arrows, duplicate, hide and remove. Each block opens to show its own fields.
 */
export default function BlockListEditor({ blocks, onChange, listId = 'blocks', emptyText }) {
  const list = blocks || [];
  const [openIds, setOpenIds] = useState(() => new Set());
  const keyOf = (block, index) => block.id || `i${index}`;

  const toggle = (key) => setOpenIds((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const insert = (type, at) => {
    const block = createBlock(type);
    const next = [...list];
    next.splice(at, 0, block);
    onChange(next);
    setOpenIds((current) => new Set(current).add(block.id));
  };

  const setBlock = (index, patch) => {
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    onChange(next);
  };

  const duplicate = (index) => {
    const clone = { ...JSON.parse(JSON.stringify(list[index])), id: newBlockId() };
    const next = [...list];
    next.splice(index + 1, 0, clone);
    onChange(next);
    setOpenIds((current) => new Set(current).add(clone.id));
  };

  const remove = (index) => {
    const meta = getBlockType(list[index].type);
    if (!window.confirm(`Remove this ${meta.label.toLowerCase()} block?`)) return;
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <div className="pbe">
      {list.length === 0 && (
        <div className="pbe-empty">
          <i className="fas fa-layer-group" aria-hidden="true" />
          <p>{emptyText || 'No extra blocks yet.'}</p>
          <span>Add text, pictures, videos, cards, a timeline, FAQs and more.</span>
        </div>
      )}
      {list.map((block, index) => {
        const meta = getBlockType(block.type);
        const key = keyOf(block, index);
        const isOpen = openIds.has(key);
        const summary = truncate(meta.summary?.(block));
        return (
          <React.Fragment key={key}>
            <SortableItem
              listId={listId}
              index={index}
              onMove={(from, to) => onChange(reorder(list, from, to))}
              className={`pbe-block${isOpen ? ' is-open' : ''}${block.hidden ? ' is-hidden' : ''}`}
            >
              <div className="pbe-block-head">
                <DragHandle listId={listId} index={index} />
                <button type="button" className="pbe-block-title" onClick={() => toggle(key)} aria-expanded={isOpen}>
                  <span className="pbe-block-icon"><i className={meta.icon} aria-hidden="true" /></span>
                  <span className="pbe-block-text">
                    <strong>{meta.label}</strong>
                    <small>{summary || 'Empty, click to fill in'}</small>
                  </span>
                  {block.hidden && <span className="admin-pill is-muted">Hidden</span>}
                  <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} pbe-block-chevron`} aria-hidden="true" />
                </button>
                <div className="pbe-block-actions">
                  <button type="button" className="admin-icon-btn" title="Move up" disabled={index === 0} onClick={() => onChange(reorder(list, index, index - 1))}>
                    <i className="fas fa-arrow-up" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" title="Move down" disabled={index === list.length - 1} onClick={() => onChange(reorder(list, index, index + 1))}>
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" title={block.hidden ? 'Show on the page' : 'Hide from the page'} onClick={() => setBlock(index, { hidden: !block.hidden })}>
                    <i className={`fas fa-eye${block.hidden ? '-slash' : ''}`} aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" title="Duplicate" onClick={() => duplicate(index)}>
                    <i className="fas fa-copy" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn danger" title="Remove" onClick={() => remove(index)}>
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="pbe-block-body">
                  {meta.fields.map((field) => (
                    <Field
                      key={field.key}
                      field={field}
                      value={block[field.key]}
                      onChange={(value) => setBlock(index, { [field.key]: value })}
                      listId={`${listId}-${key}-${field.key}`}
                    />
                  ))}
                </div>
              )}
            </SortableItem>
            {index < list.length - 1 && <AddBlockMenu compact label="Insert here" onAdd={(type) => insert(type, index + 1)} />}
          </React.Fragment>
        );
      })}
      <AddBlockMenu onAdd={(type) => insert(type, list.length)} />
    </div>
  );
}
